import React, { useState } from 'react';
import AssessmentManager from 'components/common/assessment-manager/assessment-manager';

import { useAssessmentsDataContext } from 'providers/assessments-data-provider';
import { markAssessmentStarted, markAssessmentSubmitted, submitAnswers } from 'api/assessments-api';
import { useAuth } from 'providers/auth-provider';
import { useNavigate } from 'react-router-dom';
import { symspanDefinition } from './data/symspan';
import { ospanDefinition } from './data/ospan';

const totals = {};

function WmAssessment() {
  const { wmAssessments } = useAssessmentsDataContext();
  const auth = useAuth();
  const navigate = useNavigate();
  const [sspanComplete, setSspanComplete] = useState(true);
  const [ospanComplete, setOspanComplete] = useState(false);

  const findTrialDefinitions = (wmDefinition, trial) => {
    const definitions = [];
    for (let w = 0; w < wmDefinition.length; w += 1) {
      if (wmDefinition[w]?.props?.trial === trial) {
        definitions.push(wmDefinition[w]);
      }
    }
    return definitions;
  };

  const findAssessmentQuestions = (assessment, trial) => {
    const lettersKey = `Letters ${trial}`;
    const equationKey = `Equation ${trial}`;
    const spacialKey = `Spacial ${trial}`;
    const symmetryKey = `Symmetry ${trial}`;
    const assessmentQuestions = {};
    const { questions } = assessment;
    for (let q = 0; q < questions.length; q += 1) {
      // eslint-disable-next-line default-case
      switch (questions[q].body) {
        case lettersKey:
          assessmentQuestions.letters = questions[q].id;
          break;
        case equationKey:
          assessmentQuestions.equation = questions[q].id;
          break;
        case symmetryKey:
          assessmentQuestions.symmetry = questions[q].id;
          break;
        case spacialKey:
          assessmentQuestions.spacial = questions[q].id;
          break;
      }
    }
    return assessmentQuestions;
  };

  const findAssessment = (type) => {
    for (let a = 0; a < wmAssessments.length; a += 1) {
      if (wmAssessments[a].name.toLowerCase().includes(type.toLowerCase())) {
        return wmAssessments[a];
      }
    }
    return undefined;
  };

  const calculateRecallScore = (results, trialDefinitions, trialType) => {
    for (let r = 0; r < results.length; r += 1) {
      if (trialDefinitions[r]?.type === trialType) {
        const { answers } = trialDefinitions[r].props;
        const userSelections = results[r]?.userSelections;
        if (userSelections) {
          let answeredCorrectly = 0;
          for (let a = 0; a < answers.length; a += 1) {
            const answer = userSelections[answers[a].cell];
            if (answer && answer === answers[a].index) {
              answeredCorrectly += 1;
            }
          }
          return answeredCorrectly;
        }
      }
    }
    return 0;
  };

  const calculateOSpanEquationScore = (results, trialDefinitions) => {
    let equationScore = 0;
    for (let r = 0; r < results.length; r += 1) {
      if (trialDefinitions[r]?.type === 'MATH_ANSWER') {
        const correctResult = trialDefinitions[r].props.isCorrect;
        if (correctResult === results[r].result) {
          equationScore += 1;
        }
      }
    }
    return equationScore;
  };

  const calculateSSpanSymmetryScore = (results, trialDefinitions) => {
    let symmetryScore = 0;
    for (let r = 0; r < results.length; r += 1) {
      if (trialDefinitions[r]?.type === 'SYMMETRY_GRID') {
        const correctResult = trialDefinitions[r].props.isSymmetrical;
        if (correctResult === results[r].result) {
          symmetryScore += 1;
        }
      }
    }
    return symmetryScore;
  };

  const prepareSSpanResults = (data) => {
    const { trial } = data[0];
    const assessment = findAssessment('symspan');
    const assessmentQuestions = findAssessmentQuestions(assessment, trial);
    const trialDefinitions = findTrialDefinitions(symspanDefinition.steps, trial);
    const spacialScore = calculateRecallScore(data, trialDefinitions, 'BLOCK_RECALL');
    const symmetryScore = calculateSSpanSymmetryScore(data, trialDefinitions);

    const answers = [];
    if (Object.keys(assessmentQuestions).length > 0) {
      answers.push({
        assessmentId: assessment.id,
        question: {
          id: assessmentQuestions.spacial,
        },
        answer: spacialScore,
      });
      answers.push({
        assessmentId: assessment.id,
        question: {
          id: assessmentQuestions.symmetry,
        },
        answer: symmetryScore,
      });
    }
    totals.sspan.symmetry += symmetryScore;
    totals.sspan.spacial += spacialScore;

    return {
      assessment,
      answers,
    };
  };

  const prepareSSpanTotals = () => {
    const assessment = findAssessment('symspan');
    const assessmentQuestions = findAssessmentQuestions(assessment, 'Total');
    const answers = [];
    answers.push({
      assessmentId: assessment.id,
      question: {
        id: assessmentQuestions.spacial,
      },
      answer: totals.sspan.spacial,
    });
    answers.push({
      assessmentId: assessment.id,
      question: {
        id: assessmentQuestions.symmetry,
      },
      answer: totals.sspan.symmetry,
    });

    return {
      assessment,
      answers,
    };
  };

  const resetSSpanTotals = () => {
    totals.sspan = {
      symmetry: 0,
      spacial: 0,
    };
  };

  const prepareOSpanResults = (data) => {
    const { trial } = data[0];
    const assessment = findAssessment('ospan');
    const assessmentQuestions = findAssessmentQuestions(assessment, trial);
    const trialDefinitions = findTrialDefinitions(ospanDefinition.steps, trial);
    const equationScore = calculateOSpanEquationScore(data, trialDefinitions);
    const lettersScore = calculateRecallScore(data, trialDefinitions, 'LETTER_RECALL');

    const answers = [];
    if (Object.keys(assessmentQuestions).length > 0) {
      answers.push({
        assessmentId: assessment.id,
        question: {
          id: assessmentQuestions.letters,
        },
        answer: lettersScore,
      });
      answers.push({
        assessmentId: assessment.id,
        question: {
          id: assessmentQuestions.equation,
        },
        answer: equationScore,
      });
    }
    totals.ospan.letters += lettersScore;
    totals.ospan.equation += equationScore;

    return {
      assessment,
      answers,
    };
  };

  const prepareOSpanTotals = () => {
    const assessment = findAssessment('ospan');
    const assessmentQuestions = findAssessmentQuestions(assessment, 'Total');
    const answers = [];
    answers.push({
      assessmentId: assessment.id,
      question: {
        id: assessmentQuestions.letters,
      },
      answer: totals.ospan.letters,
    });
    answers.push({
      assessmentId: assessment.id,
      question: {
        id: assessmentQuestions.equation,
      },
      answer: totals.ospan.equation,
    });

    return {
      assessment,
      answers,
    };
  };

  const resetOSpanTotals = () => {
    totals.ospan = {
      letters: 0,
      equation: 0,
    };
  };

  const handleSubmit = async (assessmentType, data) => {
    const index = data.length - 1;
    const { trial, complete } = data[index];
    if (trial === 1) {
      if (assessmentType === 'ospan') {
        resetOSpanTotals();
      } else {
        resetSSpanTotals();
      }
    }
    const trialResults = assessmentType === 'ospan' ? prepareOSpanResults(data) : prepareSSpanResults(data);
    const trialAssessment = trialResults.assessment;
    const trialAnswers = trialResults.answers;
    if (trial === 1) {
      await markAssessmentStarted(auth, trialAssessment.id);
    }
    if (complete) {
      if (trialAnswers.length) {
        await submitAnswers(auth, trialAnswers);
      }
      const completedResults = assessmentType === 'ospan' ? prepareOSpanTotals() : prepareSSpanTotals();
      const completedAssessment = completedResults.assessment;
      const completedAnswers = completedResults.answers;
      await submitAnswers(auth, completedAnswers);
      await markAssessmentSubmitted(auth, completedAssessment.id);
    } else if (trialAnswers.length) {
      await submitAnswers(auth, trialAnswers);
    }
  };

  const handleComplete = (assessment) => {
    if (assessment === 'sspan') {
      setSspanComplete(true);
    } else {
      setOspanComplete(true);
    }
  };

  if (!sspanComplete) {
    return (
      <AssessmentManager
        steps={symspanDefinition.steps}
        submitAssessment={(data) => handleSubmit('sspan', data)}
        completeAssessment={() => handleComplete('sspan')}
      />
    );
  }

  if (sspanComplete && !ospanComplete) {
    return (
      <AssessmentManager
        key={Math.random()}
        steps={ospanDefinition.steps}
        submitAssessment={(data) => handleSubmit('ospan', data)}
        completeAssessment={() => handleComplete('ospan')}
      />
    );
  }

  navigate('/');
  return <div />;
}

export default WmAssessment;
